import { DestroyRef, inject, Injectable, signal, Signal } from "@angular/core";
import { PORTAL_PRODUCT_SERVICE_TOKEN } from "../../constants";
import { LogoUrlModel, PortalStyleModel } from "../../models";
import { combineLatest, firstValueFrom, from, map, Observable, switchMap } from "rxjs";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import { ProductKey, TranslationService } from "gast-common-ng2";
import { FooterModel } from "../../../footer";
import { FloatingContactPanelModel } from "../../../floating-contact-panel";
import { FloatingButtonsModel } from "../../../floating-buttons";
import { LinkModel, TextModel } from "../../dto";
import { HeaderStyleModel } from "../../../header";

@Injectable()
export class PortalStyleService {
  readonly #portalProductService = inject(PORTAL_PRODUCT_SERVICE_TOKEN);
  readonly #translationService = inject(TranslationService);
  readonly #destroyRef = inject(DestroyRef);
  #currentSelectedProduct = signal<ProductKey | undefined>(undefined);
  #currentLanguageCode = signal<string | undefined>(undefined);

  readonly latestState$: Observable<PortalStyleModel> = combineLatest([
    this.#portalProductService.selectedProduct$,
    this.#translationService.initOrLanguageChanged$,
  ]).pipe(takeUntilDestroyed(this.#destroyRef));

  /**
   * Emits a { @link LogoUrlModel } based on the product selected by {@link PortalProductStrategy } implementation.
   */
  headerLogosResolved$: Observable<LogoUrlModel> = this.latestState$.pipe(
    map((state: PortalStyleModel): LogoUrlModel => this.#resolveHeaderLogos(...state))
  );
  /**
   * Emits signal of { @link LogoUrlModel } based on the product selected by {@link PortalProductStrategy } implementation.
   */
  headerLogosResolved: Signal<LogoUrlModel | undefined> = toSignal(this.headerLogosResolved$);
  /**
   * Emits a { @link FooterModel } based on the product selected by {@link PortalProductStrategy } implementation.
   */
  footerModelResolved$: Observable<FooterModel> = this.latestState$.pipe(
    map((state: PortalStyleModel): FooterModel => this.#resolveFooterModel(...state))
  );
  /**
   * Emits a { @link HeaderStyleModel } based on the product selected by {@link PortalProductStrategy } implementation.
   */
  headerStyleResolved$: Observable<HeaderStyleModel> = this.#portalProductService.selectedProduct$.pipe(
    takeUntilDestroyed(this.#destroyRef),
    map(this.#resolveHeaderStyle)
  );
  /**
   * Emits signal of { @link HeaderStyleModel } based on the product selected by {@link PortalProductStrategy } implementation.
   */
  headerStyleResolved = toSignal(this.headerStyleResolved$, { initialValue: {} as HeaderStyleModel });

  /**
   * Emits signal of { @link FooterModel } based on the product selected by {@link PortalProductStrategy } implementation.
   */
  footerModelResolved: Signal<FooterModel> = toSignal(this.footerModelResolved$, { initialValue: {} as FooterModel });
  /**
   * Emits a { @link FloatingContactPanelModel} in case it is supported for the current product or undefined if not.
   */
  floatingContactPanelModelResolved$: Observable<FloatingContactPanelModel | undefined> = this.latestState$.pipe(
    takeUntilDestroyed(this.#destroyRef),
    switchMap((state: PortalStyleModel) => {
      return from(this.#resolveFloatingContactPanelModel(...state));
    })
  );
  /**
   * Emits signal of { @link FloatingContactPanelModel} in case it is supported for the current product or undefined if not.
   */
  floatingContactPanelModelResolved: Signal<FloatingContactPanelModel | undefined> = toSignal(
    this.floatingContactPanelModelResolved$
  );
  /**
   * Emits a { @link FloatingButtonsModel } in case it is supported for the current product or undefined if not.
   */
  floatingButtonsModelResolved$: Observable<FloatingButtonsModel | undefined> =
    this.#portalProductService.selectedProduct$.pipe(
      takeUntilDestroyed(this.#destroyRef),
      map(this.#resolveFloatingButtonsModel)
    );
  /**
   * Emits signal of { @link FloatingButtonsModel } in case it is supported for the current product or undefined if not.
   */
  floatingButtonsModelResolved: Signal<FloatingButtonsModel | undefined> = toSignal(this.floatingButtonsModelResolved$);

  constructor() {
    this.latestState$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((latestState: PortalStyleModel) => {
      const [product, currentLanguage] = latestState;
      this.#setSiteFavicon(product);
      this.#currentSelectedProduct.set(product);
      this.#currentLanguageCode.set(currentLanguage);
    });
  }

  /**
   * Calls the external landing page based on the current selected product
   */
  onHeaderLogoPressed(): void {
    if (!this.#currentSelectedProduct() || !this.#currentLanguageCode()) {
      throw new Error("Cannot resolve product or current language.");
    }
    switch (this.#currentSelectedProduct()) {
      case "GENERAL": {
        window.open("https://www.gast.de/", "_blank");
        break;
      }
      case "TESTDAF": {
        window.open("https://www.testdaf.de", "_blank");
        break;
      }
      case "TESTAS":
      case "DIGITAL_TESTAS": {
        const url: string =
          this.#currentLanguageCode() === "en" ? "https://www.testas.de/en/" : "https://www.testas.de/de/";
        window.open(url, "_blank");
        break;
      }
      case "ONSET": {
        const url: string = this.#currentLanguageCode() === "en" ? "https://www.onset.de/en/" : "https://www.onset.de";
        window.open(url, "_blank");
        break;
      }
      case "DUO": {
        const url: string =
          this.#currentLanguageCode() === "en" ? "https://www.deutsch-uni.com/en/" : "https://www.deutsch-uni.com/";
        window.open(url, "_blank");
        break;
      }
      case "DMAT": {
        const url: string = this.#currentLanguageCode() === "en" ? "https://www.d-mat.de/en/" : "https://www.d-mat.de/";
        window.open(url, "_blank");
        break;
      }
      case "INL": {
        let url: string;
        if (
          this.#currentLanguageCode() === "en" ||
          this.#currentLanguageCode() === "de" ||
          this.#currentLanguageCode() === "fr" ||
          this.#currentLanguageCode() === "lu"
        ) {
          url = `https://www.inll.lu/${this.#currentLanguageCode()}/`;
        } else {
          url = "https://www.inll.lu";
        }
        window.open(url, "_blank");
        break;
      }
      case "DTZ": {
        window.open(
          "https://www.gast.de/de/forschung-entwicklung/entwicklung/auftraege/deutsch-test-fuer-zuwanderer-dtz",
          "_blank"
        );
        break;
      }
    }
  }

  #resolveHeaderStyle(product: ProductKey): HeaderStyleModel {
    switch (product) {
      case "GENERAL":
      case "TESTAS":
      case "DIGITAL_TESTAS":
      case "DMAT":
      case "ONSET":
      case "DUO":
      case "GJUDAF":
        return {
          languageDropdownVisible: true,
          loginDropdownVisible: true,
        };
      case "TESTDAF":
      case "INL":
      case "DTZ":
        return {
          languageDropdownVisible: false,
          loginDropdownVisible: true,
        };
    }
  }

  #resolveHeaderLogos(product: ProductKey, currentLanguageCode: string): LogoUrlModel {
    switch (product) {
      case "GENERAL":
        return {
          logoBigUrl: "./portal-assets/img/gast/gast-logo.svg",
          logoSmallUrl: "./portal-assets/img/gast/gast-logo.svg",
        };
      case "TESTDAF":
        return {
          logoBigUrl: "./portal-assets/img/testdaf/testdaf-logo.svg",
          logoSmallUrl: "./portal-assets/img/testdaf/testdaf-logo-inline.svg",
        };
      case "TESTAS":
      case "DIGITAL_TESTAS": {
        const bigTestAsLogo: string =
          currentLanguageCode === "en"
            ? "./portal-assets/img/testas/testas-logo-en.svg"
            : "./portal-assets/img/testas/testas-logo-de.svg";
        return {
          logoBigUrl: bigTestAsLogo,
          logoSmallUrl: "./portal-assets/img/testas/testas-logo-inline.svg",
        };
      }
      case "ONSET":
        return {
          logoBigUrl: "./portal-assets/img/onset/onset-logo.svg",
          logoSmallUrl: "./portal-assets/img/onset/onset-logo-inline.svg",
        };
      case "DUO":
        return {
          logoBigUrl: "./portal-assets/img/duo/duo-logo.svg",
          logoSmallUrl: "./portal-assets/img/duo/duo-logo-inline.svg",
        };
      case "INL":
        return {
          logoBigUrl: "./portal-assets/img/inl/testlaf-logo.svg",
          logoSmallUrl: "./portal-assets/img/inl/inl-logo-inline.png",
        };
      case "DMAT":
        return {
          logoBigUrl: "./portal-assets/img/dmat/dmat-logo.svg",
          logoSmallUrl: "./portal-assets/img/dmat/dmat-logo-inline.svg",
        };
      case "DTZ":
        return {
          logoBigUrl: "./portal-assets/img/dtz/dtz-logo.png",
          logoSmallUrl: "./portal-assets/img/dtz/dtz-logo.png",
        };
      default:
        return {
          logoBigUrl: "./portal-assets/img/gast/gast-logo.svg",
          logoSmallUrl: "./portal-assets/img/gast/gast-logo.svg",
        };
    }
  }

  #resolveFooterModel(product: ProductKey, currentLanguageCode: string): FooterModel {
    const gastFooterModel: FooterModel = {
      descriptionTitle: { text: "GAST-PORTAL-NG.FOOTER-COMPONENT.ABOUT_GAST.DESCRIPTION_TITLE", useTranslation: true },
      descriptionText: { text: "GAST-PORTAL-NG.FOOTER-COMPONENT.ABOUT_GAST.DESCRIPTION_TEXT", useTranslation: true },
      quickLinksTitle: { text: "GAST-PORTAL-NG.FOOTER-COMPONENT.QUICK_LINKS.TITLE", useTranslation: true },
      quickLinks: [
        {
          text: "GAST-PORTAL-NG.FOOTER-COMPONENT.QUICK_LINKS.PRODUCTS",
          useTranslation: true,
          linkUrl: "https://www.gast.de/de/produkte",
        },
        {
          text: "GAST-PORTAL-NG.FOOTER-COMPONENT.QUICK_LINKS.RESEARCH_AND_DEVELOPMENT",
          useTranslation: true,
          linkUrl: "https://www.gast.de/de/forschung-entwicklung",
        },
        {
          text: "GAST-PORTAL-NG.FOOTER-COMPONENT.QUICK_LINKS.GAST_ACADEMY",
          useTranslation: true,
          linkUrl: "https://www.gast.de/de/gast-akademie",
        },
        {
          text: "GAST-PORTAL-NG.FOOTER-COMPONENT.QUICK_LINKS.ABOUT_GAST",
          useTranslation: true,
          linkUrl: "https://www.gast.de/de/ueber-gast",
        },
        {
          text: "GAST-PORTAL-NG.FOOTER-COMPONENT.QUICK_LINKS.CAREER",
          useTranslation: true,
          linkUrl: "https://www.gast.de/de/karriere",
        },
        {
          text: "GAST-PORTAL-NG.FOOTER-COMPONENT.QUICK_LINKS.NEWS",
          useTranslation: true,
          linkUrl: "https://www.gast.de/de/aktuelles",
        },
        { text: "TestDaF", useTranslation: false, linkUrl: "https://www.testdaf.de/de/" },
        { text: "onSET", useTranslation: false, linkUrl: `https://www.onset.de/${currentLanguageCode}/` },
        { text: "TestAS", useTranslation: false, linkUrl: `https://www.testas.de/${currentLanguageCode}/` },
        { text: "dMAT", useTranslation: false, linkUrl: "https://www.d-mat.de/" },
        {
          text: "Deutsch-Uni Online",
          useTranslation: false,
          linkUrl: `https://www.deutsch-uni.com/${currentLanguageCode}/`,
        },
      ],
      serviceLinksTitle: { text: "GAST-PORTAL-NG.FOOTER-COMPONENT.SERVICE_LINKS.TITLE", useTranslation: true },
      serviceLinks: [
        {
          text: "GAST-PORTAL-NG.FOOTER-COMPONENT.SERVICE_LINKS.RESEARCH_INQUIRY",
          useTranslation: true,
          linkUrl: "https://www.gast.de/de/forschung-entwicklung/forschung/forschungsanfragen",
        },
        {
          text: "GAST-PORTAL-NG.FOOTER-COMPONENT.SERVICE_LINKS.BECOME_TEST_CENTER",
          useTranslation: true,
          linkUrl: "https://www.gast.de/de/ueber-gast/netzwerk/testzentrum-werden-lizenzierung",
        },
        {
          text: "GAST-PORTAL-NG.FOOTER-COMPONENT.SERVICE_LINKS.PAYMENT_OPTIONS",
          useTranslation: true,
          linkUrl: "https://www.gast.de/de/zahlungsmoeglichkeiten-bei-gast",
        },
      ],
      contactLinksTitle: { text: "GAST-PORTAL-NG.FOOTER-COMPONENT.CONTACT_LINKS.TITLE", useTranslation: true },
      contactLinks: [
        {
          text: "GAST-PORTAL-NG.FOOTER-COMPONENT.CONTACT_LINKS.IMPRINT",
          useTranslation: true,
          linkUrl: "https://www.gast.de/de/kontakt/impressum",
        },
        {
          text: "GAST-PORTAL-NG.FOOTER-COMPONENT.CONTACT_LINKS.DATA_PROTECTION",
          useTranslation: true,
          linkUrl: "https://www.gast.de/de/kontakt/datenschutz",
        },
        {
          text: "GAST-PORTAL-NG.FOOTER-COMPONENT.CONTACT_LINKS.CONDITIONS",
          useTranslation: true,
          linkUrl: "https://www.gast.de/de/kontakt/agb",
        },
      ],
      facebookUrl: "",
      youtubeUrl: "",
      twitterUrl: "",
      instagramUrl: "https://www.instagram.com/gast_ev/",
      gastOfferText: { text: "", useTranslation: false },
      showALTELogo: true,
    };
    switch (product) {
      case "GENERAL":
        return gastFooterModel;
      case "TESTDAF":
        return {
          descriptionTitle: {
            text: "GAST-PORTAL-NG.FOOTER-COMPONENT.ABOUT_TEST_DAF.DESCRIPTION_TITLE",
            useTranslation: true,
          },
          descriptionText: {
            text: "GAST-PORTAL-NG.FOOTER-COMPONENT.ABOUT_TEST_DAF.DESCRIPTION_TEXT",
            useTranslation: true,
          },
          quickLinksTitle: { text: "GAST-PORTAL-NG.FOOTER-COMPONENT.QUICK_LINKS.TITLE", useTranslation: true },
          quickLinks: [
            {
              text: "GAST-PORTAL-NG.FOOTER-COMPONENT.QUICK_LINKS.PARTICIPANTS",
              useTranslation: true,
              linkUrl:
                "https://www.testdaf.de/de/teilnehmende/mein-testdaf/mit-dem-testdaf-an-eine-deutsche-hochschule/",
            },
            {
              text: "GAST-PORTAL-NG.FOOTER-COMPONENT.QUICK_LINKS.UNIVERSITIES",
              useTranslation: true,
              linkUrl:
                "https://www.testdaf.de/de/hochschulen/der-testdaf-und-hochschulen/der-testdaf-an-ihrer-hochschule/",
            },
            {
              text: "GAST-PORTAL-NG.FOOTER-COMPONENT.QUICK_LINKS.TEST_CENTERS",
              useTranslation: true,
              linkUrl:
                "https://www.testdaf.de/de/testzentren/der-testdaf-an-ihrem-testzentrum/die-testzentren-von-gast/",
            },
            {
              text: "GAST-PORTAL-NG.FOOTER-COMPONENT.QUICK_LINKS.TEACHERS",
              useTranslation: true,
              linkUrl: "https://www.testdaf.de/de/lehrkraefte/",
            },
            {
              text: "GAST-PORTAL-NG.FOOTER-COMPONENT.QUICK_LINKS.RESEARCH",
              useTranslation: true,
              linkUrl: "https://www.testdaf.de/de/forschung/",
            },
            {
              text: "GAST-PORTAL-NG.FOOTER-COMPONENT.QUICK_LINKS.ABOUT_US",
              useTranslation: true,
              linkUrl: "https://www.testdaf.de/de/ueber-testdaf/ueber-gast/testdaf-institut/",
            },
            {
              text: "GAST-PORTAL-NG.FOOTER-COMPONENT.QUICK_LINKS.GAST",
              useTranslation: true,
              linkUrl: "https://www.gast.de/",
            },
            {
              text: "GAST-PORTAL-NG.FOOTER-COMPONENT.QUICK_LINKS.GAST_ACADEMY",
              useTranslation: true,
              linkUrl: "https://www.gast.de/de/gast-akademie/",
            },
            { text: "onSET", useTranslation: false, linkUrl: `https://www.onset.de/${currentLanguageCode}/` },
            { text: "TestAS", useTranslation: false, linkUrl: `https://www.testas.de/${currentLanguageCode}/` },
            { text: "dMAT", useTranslation: false, linkUrl: "https://www.d-mat.de/" },
            {
              text: "Deutsch-Uni Online",
              useTranslation: false,
              linkUrl: `https://www.deutsch-uni.com/${currentLanguageCode}/`,
            },
          ],
          serviceLinksTitle: { text: "GAST-PORTAL-NG.FOOTER-COMPONENT.SERVICE_LINKS.TITLE", useTranslation: true },
          serviceLinks: [
            {
              text: "GAST-PORTAL-NG.FOOTER-COMPONENT.SERVICE_LINKS.FAQ",
              useTranslation: true,
              linkUrl: `https://www.testdaf.de/de/teilnehmende/mein-testdaf/faq/`,
            },
            {
              text: "GAST-PORTAL-NG.FOOTER-COMPONENT.SERVICE_LINKS.DATES_AND_REGISTRATION",
              useTranslation: true,
              linkUrl: `https://www.testdaf.de/de/teilnehmende/mein-testdaf/testdaf-termine-und-anmeldung/`,
            },
            {
              text: "GAST-PORTAL-NG.FOOTER-COMPONENT.SERVICE_LINKS.FIND_A_TEST_CENTER",
              useTranslation: true,
              linkUrl: `https://www.testdaf.de/de/teilnehmende/mein-testdaf/testzentrum-finden/`,
            },
            {
              text: "GAST-PORTAL-NG.FOOTER-COMPONENT.SERVICE_LINKS.EXAMINATION_REGULATIONS",
              useTranslation: true,
              linkUrl: `https://www.testdaf.de/fileadmin/testdaf/downloads/Dokumente/TestDaF-Pruefungsordnung_10-2020.pdf`,
            },
            {
              text: "GAST-PORTAL-NG.FOOTER-COMPONENT.SERVICE_LINKS.EXAM_RULES",
              useTranslation: true,
              linkUrl: `https://www.testdaf.de/fileadmin/testdaf/downloads/Dokumente/TestDaF_Pruefungsregeln_10-2020.pdf`,
            },
            {
              text: "GAST-PORTAL-NG.FOOTER-COMPONENT.SERVICE_LINKS.INFORMATIONAL_MATERIAL",
              useTranslation: true,
              linkUrl: `https://www.testdaf.de/de/ueber-testdaf/testdaf-ein-produkt-der-gast/informationsmaterialien/`,
            },
            {
              text: "GAST-PORTAL-NG.FOOTER-COMPONENT.SERVICE_LINKS.READY_FOR_TEST_DAF",
              useTranslation: true,
              linkUrl: `https://www2.testdaf.de/teilnehmer/tn-vorbereitung_test.php?id=1`,
            },
            {
              text: "GAST-PORTAL-NG.FOOTER-COMPONENT.SERVICE_LINKS.BECOME_TEST_CENTER",
              useTranslation: true,
              linkUrl: "https://www.gast.de/de/ueber-gast/netzwerk/testzentrum-werden-lizenzierung",
            },
            {
              text: "GAST-PORTAL-NG.FOOTER-COMPONENT.SERVICE_LINKS.RESEARCH_INQUIRY",
              useTranslation: true,
              linkUrl: "https://www.gast.de/de/forschung-entwicklung/forschung/forschungsanfragen",
            },
            {
              text: "GAST-PORTAL-NG.FOOTER-COMPONENT.SERVICE_LINKS.INFO_IN_MY_LANGUAGE",
              useTranslation: true,
              linkUrl: "https://www.testdaf.de/de/teilnehmende/warum-testdaf/informationen-in-ihrer-sprache/",
            },
            {
              text: "GAST-PORTAL-NG.FOOTER-COMPONENT.SERVICE_LINKS.GAST_NEWSLETTER_SUBSCRIPTION",
              useTranslation: true,
              linkUrl: "http://webexpress.retarus.com/WebExpressSMS/subscribe/76L-24U-6MZB",
            },
          ],
          contactLinksTitle: { text: "GAST-PORTAL-NG.FOOTER-COMPONENT.CONTACT_LINKS.TITLE", useTranslation: true },
          contactLinks: [
            {
              text: "GAST-PORTAL-NG.FOOTER-COMPONENT.CONTACT_LINKS.CONTACT",
              useTranslation: true,
              linkUrl: "https://www.testdaf.de/de/ueber-testdaf/kontakt/kontakt-und-besucherinformationen/",
            },
            {
              text: "GAST-PORTAL-NG.FOOTER-COMPONENT.CONTACT_LINKS.IMPRINT",
              useTranslation: true,
              linkUrl: "https://www.testdaf.de/de/impressum/",
            },
            {
              text: "GAST-PORTAL-NG.FOOTER-COMPONENT.CONTACT_LINKS.DATA_PROTECTION",
              useTranslation: true,
              linkUrl: "https://www.testdaf.de/de/datenschutzerklaerung/",
            },
            {
              text: "GAST-PORTAL-NG.FOOTER-COMPONENT.CONTACT_LINKS.CONDITIONS",
              useTranslation: true,
              linkUrl: "https://www.testdaf.de/de/agb/",
            },
          ],
          facebookUrl: "https://www.facebook.com/TestDaF/",
          youtubeUrl: "",
          twitterUrl: "",
          instagramUrl: "https://www.instagram.com/gast_ev/",
          gastOfferText: { text: "GAST-PORTAL-NG.FOOTER-COMPONENT.GAST_TEST_DAF_OFFER_TEXT", useTranslation: true },
          showALTELogo: true,
        };
      case "TESTAS":
      case "DIGITAL_TESTAS": {
        return {
          descriptionTitle: {
            text: "GAST-PORTAL-NG.FOOTER-COMPONENT.ABOUT_TEST_AS.DESCRIPTION_TITLE",
            useTranslation: true,
          },
          descriptionText: {
            text: "GAST-PORTAL-NG.FOOTER-COMPONENT.ABOUT_TEST_AS.DESCRIPTION_TEXT",
            useTranslation: true,
          },
          quickLinksTitle: { text: "GAST-PORTAL-NG.FOOTER-COMPONENT.QUICK_LINKS.TITLE", useTranslation: true },
          quickLinks: [
            {
              text: "GAST-PORTAL-NG.FOOTER-COMPONENT.QUICK_LINKS.PARTICIPANTS",
              useTranslation: true,
              linkUrl:
                currentLanguageCode === "en"
                  ? "https://www.testas.de/en/participants/my-testas/with-the-testas-to-a-german-university"
                  : `https://www.testas.de/de/teilnehmende/mit-dem-testas-an-eine-deutsche-hochschule`,
            },
            {
              text: "GAST-PORTAL-NG.FOOTER-COMPONENT.QUICK_LINKS.UNIVERSITIES",
              useTranslation: true,
              linkUrl:
                currentLanguageCode === "en"
                  ? "https://www.testas.de/en/universities/testas-and-universities/the-testas-at-your-university"
                  : `https://www.testas.de/de/hochschulen/der-testas-und-hochschulen/der-testas-an-ihrer-hochschule`,
            },
            {
              text: "GAST-PORTAL-NG.FOOTER-COMPONENT.QUICK_LINKS.TEST_CENTERS",
              useTranslation: true,
              linkUrl:
                currentLanguageCode === "en"
                  ? "https://www.testas.de/en/test-centres/the-testas-and-test-centres/test-centres-of-gast"
                  : `https://www.testas.de/de/testzentren/der-testas-und-testzentren/die-testzentren-von-gast`,
            },
            {
              text: "GAST-PORTAL-NG.FOOTER-COMPONENT.QUICK_LINKS.RESEARCH",
              useTranslation: true,
              linkUrl:
                currentLanguageCode === "en"
                  ? "https://www.testas.de/en/research/research-at-gast/testas-from-a-scientific-perspective"
                  : `https://www.testas.de/de/ueber-uns/testas-ein-produkt-der-gast/testas-aus-wissenschaftlicher-sicht`,
            },
            {
              text: "GAST-PORTAL-NG.FOOTER-COMPONENT.QUICK_LINKS.ABOUT_US",
              useTranslation: true,
              linkUrl: currentLanguageCode === "en" ? "" : "https://www.gast.de/de/ueber-gast/organisation",
            },
            {
              text: "GAST-PORTAL-NG.FOOTER-COMPONENT.QUICK_LINKS.GAST",
              useTranslation: true,
              linkUrl: "https://www.gast.de/de/",
            },
            {
              text: "GAST-PORTAL-NG.FOOTER-COMPONENT.QUICK_LINKS.GAST_ACADEMY",
              useTranslation: true,
              linkUrl: "https://www.gast.de/de/gast-akademie",
            },
            { text: "TestDaF", useTranslation: false, linkUrl: "https://www.testdaf.de/de/" },
            { text: "onSET", useTranslation: false, linkUrl: `https://www.onset.de/${currentLanguageCode}/` },
            { text: "dMAT", useTranslation: false, linkUrl: "https://www.d-mat.de/" },
            {
              text: "Deutsch-Uni Online",
              useTranslation: false,
              linkUrl: `https://www.deutsch-uni.com/${currentLanguageCode}/`,
            },
          ],
          serviceLinksTitle: { text: "GAST-PORTAL-NG.FOOTER-COMPONENT.SERVICE_LINKS.TITLE", useTranslation: true },
          serviceLinks: [
            {
              text: "GAST-PORTAL-NG.FOOTER-COMPONENT.SERVICE_LINKS.FAQ",
              useTranslation: true,
              linkUrl:
                currentLanguageCode === "en"
                  ? "https://www.testas.de/en/participants/my-testas/faq"
                  : "https://www.testas.de/de/teilnehmende/mit-dem-testas-an-eine-deutsche-hochschule/faq",
            },
            {
              text: "GAST-PORTAL-NG.FOOTER-COMPONENT.SERVICE_LINKS.DATES_AND_REGISTRATION",
              useTranslation: true,
              linkUrl:
                currentLanguageCode === "en"
                  ? "https://www.testas.de/en/participants/my-testas/testas-dates-and-registration"
                  : "https://www.testas.de/de/teilnehmende/mein-testas/testas-termine-und-anmeldung",
            },
            {
              text: "GAST-PORTAL-NG.FOOTER-COMPONENT.SERVICE_LINKS.FIND_A_TEST_CENTER",
              useTranslation: true,
              linkUrl:
                currentLanguageCode === "en"
                  ? "https://www.testas.de/en/participants/my-testas/find-a-test-center"
                  : "https://www.testas.de/de/teilnehmende/mein-testas/testzentrum-finden",
            },
            {
              text: "GAST-PORTAL-NG.FOOTER-COMPONENT.SERVICE_LINKS.BECOME_TEST_CENTER",
              useTranslation: true,
              linkUrl:
                currentLanguageCode === "en"
                  ? "https://www.testas.de/en/test-centres/the-testas-and-test-centres/test-centres-of-gast"
                  : "https://www.testas.de/de/testzentren/testzentrum-werden/testzentrum-werden",
            },
            {
              text: "GAST-PORTAL-NG.FOOTER-COMPONENT.SERVICE_LINKS.PAYMENT_OPTIONS",
              useTranslation: true,
              linkUrl:
                currentLanguageCode === "en"
                  ? "https://www.gast.de/de/zahlungsmoeglichkeiten-bei-gast/payment-options-at-gast#c3315"
                  : "https://www.gast.de/de/zahlungsmoeglichkeiten-bei-gast",
            },
          ],
          contactLinksTitle: { text: "GAST-PORTAL-NG.FOOTER-COMPONENT.CONTACT_LINKS.TITLE", useTranslation: true },
          contactLinks: [
            {
              text: "GAST-PORTAL-NG.FOOTER-COMPONENT.CONTACT_LINKS.IMPRINT",
              useTranslation: true,
              linkUrl:
                currentLanguageCode === "en"
                  ? "https://www.testas.de/en/imprint"
                  : "https://www.testas.de/de/impressum",
            },
            {
              text: "GAST-PORTAL-NG.FOOTER-COMPONENT.CONTACT_LINKS.DATA_PROTECTION",
              useTranslation: true,
              linkUrl:
                currentLanguageCode === "en"
                  ? "https://www.testas.de/en/data-protection"
                  : "https://www.testas.de/de/datenschutz",
            },
            {
              text: "GAST-PORTAL-NG.FOOTER-COMPONENT.CONTACT_LINKS.CONDITIONS",
              useTranslation: true,
              linkUrl:
                currentLanguageCode === "en"
                  ? "https://www.testas.de/en/general-terms-and-conditions"
                  : "https://www.testas.de/de/agb",
            },
          ],
          facebookUrl: "https://www.facebook.com/TestDaF/",
          youtubeUrl: "",
          twitterUrl: "",
          instagramUrl: "https://www.instagram.com/gast_ev/",
          gastOfferText: { text: "GAST-PORTAL-NG.FOOTER-COMPONENT.GAST_TEST_AS_OFFER_TEXT", useTranslation: true },
        } as FooterModel;
      }
      case "ONSET":
        return {
          descriptionTitle: {
            text: "GAST-PORTAL-NG.FOOTER-COMPONENT.ABOUT_ONSET.DESCRIPTION_TITLE",
            useTranslation: true,
          },
          descriptionText: {
            text: "GAST-PORTAL-NG.FOOTER-COMPONENT.ABOUT_ONSET.DESCRIPTION_TEXT",
            useTranslation: true,
          },
          quickLinksTitle: { text: "GAST-PORTAL-NG.FOOTER-COMPONENT.QUICK_LINKS.TITLE", useTranslation: true },
          quickLinks: [
            {
              text: "GAST-PORTAL-NG.FOOTER-COMPONENT.QUICK_LINKS.INSTITUTIONS",
              useTranslation: true,
              linkUrl:
                currentLanguageCode === "en"
                  ? "https://www.onset.de/en/language-placement-test-english-onset/institutions/"
                  : "https://www.onset.de/de/home/institutionen/",
            },
            {
              text: "GAST-PORTAL-NG.FOOTER-COMPONENT.QUICK_LINKS.PARTICIPANTS",
              useTranslation: true,
              linkUrl:
                currentLanguageCode === "en"
                  ? "https://www.onset.de/en/language-placement-test-english-onset/participants/"
                  : "https://www.onset.de/de/home/teilnehmende/",
            },
            {
              text: "GAST-PORTAL-NG.FOOTER-COMPONENT.QUICK_LINKS.RESEARCH",
              useTranslation: true,
              linkUrl:
                currentLanguageCode === "en"
                  ? "https://www.onset.de/en/language-placement-test-english-onset/research/"
                  : "https://www.onset.de/de/home/forschung/",
            },
            {
              text: "GAST-PORTAL-NG.FOOTER-COMPONENT.QUICK_LINKS.ABOUT_ONSET",
              useTranslation: true,
              linkUrl:
                currentLanguageCode === "en"
                  ? "https://www.onset.de/en/language-placement-test-english-onset/about-onset/"
                  : "https://www.onset.de/de/home/ueber-onset/",
            },
            {
              text: "GAST-PORTAL-NG.FOOTER-COMPONENT.QUICK_LINKS.FREQUENT_QUESTIONS",
              useTranslation: true,
              linkUrl:
                currentLanguageCode === "en"
                  ? "https://www.onset.de/en/language-placement-test-english-onset/faq/"
                  : "https://www.onset.de/de/home/haeufige-fragen-onset-spracheinstufungstest/",
            },
          ],
          serviceLinksTitle: { text: "GAST-PORTAL-NG.FOOTER-COMPONENT.SERVICE_LINKS.TITLE", useTranslation: true },
          serviceLinks: [
            {
              text: "GAST-PORTAL-NG.FOOTER-COMPONENT.SERVICE_LINKS.BECOME_TEST_CENTER",
              useTranslation: true,
              linkUrl:
                currentLanguageCode === "en"
                  ? "http://www2.testdaf.de/ondaf/lizenz/"
                  : "https://www2.testdaf.de/ondaf/lizenz/",
            },
            {
              text: "GAST-PORTAL-NG.FOOTER-COMPONENT.SERVICE_LINKS.TEST_ACCESS",
              useTranslation: true,
              linkUrl:
                currentLanguageCode === "en"
                  ? "https://www.onset.de/en/language-placement-test-english-onset/trial-access/"
                  : "https://www.onset.de/de/home/probezugang/",
            },
            {
              text: "GAST-PORTAL-NG.FOOTER-COMPONENT.SERVICE_LINKS.EXAMPLE_TEST",
              useTranslation: true,
              linkUrl:
                currentLanguageCode === "en"
                  ? "https://www.onset.de/nc/en/language-placement-test-english-onset/sample-test/"
                  : "https://www.onset.de/nc/de/home/beispieltest-onset-online-spracheinstufungstest/",
            },
            {
              text: "GAST-PORTAL-NG.FOOTER-COMPONENT.SERVICE_LINKS.CHECK_CERTIFICATE",
              useTranslation: true,
              linkUrl:
                currentLanguageCode === "en"
                  ? "https://www.onset.de/en/language-placement-test-english-onset/check/"
                  : "https://www.onset.de/de/home/check/",
            },
            {
              text: "GAST-PORTAL-NG.FOOTER-COMPONENT.SERVICE_LINKS.TESTCENTERS_AND_DATES",
              useTranslation: true,
              linkUrl:
                currentLanguageCode === "en"
                  ? "https://www.onset.de/en/language-placement-test-english-onset/testcentres-dates/"
                  : "https://www.onset.de/de/home/testzentren-und-termine-onset-spracheinstufungstest/",
            },
          ],
          contactLinksTitle: { text: "GAST-PORTAL-NG.FOOTER-COMPONENT.CONTACT_LINKS.TITLE", useTranslation: true },
          contactLinks: [
            {
              text: "GAST-PORTAL-NG.FOOTER-COMPONENT.CONTACT_LINKS.IMPRINT",
              useTranslation: true,
              linkUrl:
                currentLanguageCode === "en"
                  ? "https://www.onset.de/en/language-placement-test-english-onset/publication-data/"
                  : "https://www.onset.de/de/home/impressum/",
            },
            {
              text: "GAST-PORTAL-NG.FOOTER-COMPONENT.CONTACT_LINKS.DATA_PROTECTION",
              useTranslation: true,
              linkUrl:
                currentLanguageCode === "en"
                  ? "https://www.onset.de/en/language-placement-test-english-onset/data-protection/"
                  : "https://www.onset.de/de/home/datenschutz-onset-spracheinstufungstest/",
            },
          ],
          facebookUrl: "https://www.facebook.com/onset.de",
          youtubeUrl: "",
          twitterUrl: "",
          instagramUrl: "https://www.instagram.com/gast_ev/",
          gastOfferText: { text: "GAST-PORTAL-NG.FOOTER-COMPONENT.GAST_ONSET_OFFER_TEXT", useTranslation: true },
        } as FooterModel;
      case "DUO": {
        const quickLinks = [
          {
            text: "GAST-PORTAL-NG.FOOTER-COMPONENT.QUICK_LINKS.GERMAN_COURSES",
            useTranslation: true,
            linkUrl:
              currentLanguageCode === "en"
                ? `https://www.deutsch-uni.com/en/german-classes/`
                : `https://www.deutsch-uni.com/de/deutschkurse/`,
          },
          {
            text: "GAST-PORTAL-NG.FOOTER-COMPONENT.QUICK_LINKS.DUO_FOR_INSTITUTIONS",
            useTranslation: true,
            linkUrl:
              currentLanguageCode === "en"
                ? `https://www.deutsch-uni.com/en/institutions/`
                : `https://www.deutsch-uni.com/de/institutionen/`,
          },
          {
            text: "GAST-PORTAL-NG.FOOTER-COMPONENT.QUICK_LINKS.LEARN_WITH_DUO",
            useTranslation: true,
            linkUrl:
              currentLanguageCode === "en"
                ? `https://www.deutsch-uni.com/en/learning-with-duo/`
                : `https://www.deutsch-uni.com/de/lernen-mit-duo/`,
          },
          {
            text: "GAST-PORTAL-NG.FOOTER-COMPONENT.QUICK_LINKS.SELF_ASSESSMENT",
            useTranslation: true,
            linkUrl:
              currentLanguageCode === "en"
                ? `https://www.deutsch-uni.com/en/self-assessment/`
                : `https://www.deutsch-uni.com/de/selbsteinschaetzung/`,
          },
        ];
        if (currentLanguageCode === "de") {
          quickLinks.push({
            text: "GAST-PORTAL-NG.FOOTER-COMPONENT.QUICK_LINKS.JOB_OFFERS",
            useTranslation: true,
            linkUrl: `https://gast-ev.jobs.personio.de/`,
          });
        }

        return {
          descriptionTitle: {
            text: "GAST-PORTAL-NG.FOOTER-COMPONENT.ABOUT_DUO.DESCRIPTION_TITLE",
            useTranslation: true,
          },
          descriptionText: {
            text: "GAST-PORTAL-NG.FOOTER-COMPONENT.ABOUT_DUO.DESCRIPTION_TEXT",
            useTranslation: true,
          },
          quickLinksTitle: { text: "GAST-PORTAL-NG.FOOTER-COMPONENT.QUICK_LINKS.TITLE", useTranslation: true },
          quickLinks: quickLinks,
          serviceLinksTitle: { text: "GAST-PORTAL-NG.FOOTER-COMPONENT.SERVICE_LINKS.TITLE", useTranslation: true },
          serviceLinks: [
            {
              text: "GAST-PORTAL-NG.FOOTER-COMPONENT.SERVICE_LINKS.FAQ",
              useTranslation: true,
              linkUrl: `https://www.deutsch-uni.com/${currentLanguageCode}/faq/`,
            },
            {
              text: "GAST-PORTAL-NG.FOOTER-COMPONENT.SERVICE_LINKS.SYSTEM_REQUIREMENTS",
              useTranslation: true,
              linkUrl:
                currentLanguageCode === "en"
                  ? `https://www.deutsch-uni.com/en/systemrequirements/`
                  : `https://www.deutsch-uni.com/de/systemvoraussetzungen/`,
            },
            {
              text: "GAST-PORTAL-NG.FOOTER-COMPONENT.SERVICE_LINKS.BROWSERCHECK",
              useTranslation: true,
              linkUrl: `https://www.deutsch-uni.com/support/konfiguration/index?lang=${currentLanguageCode}`,
            },
          ],
          contactLinksTitle: { text: "GAST-PORTAL-NG.FOOTER-COMPONENT.CONTACT_LINKS.TITLE", useTranslation: true },
          contactLinks: [
            {
              text: "GAST-PORTAL-NG.FOOTER-COMPONENT.CONTACT_LINKS.YOUR_CONTACT_PERSON",
              useTranslation: true,
              linkUrl:
                currentLanguageCode === "en"
                  ? `https://www.deutsch-uni.com/en/about-duo/#c524`
                  : `https://www.deutsch-uni.com/de/ueber-duo/#c524`,
            },
            {
              text: "GAST-PORTAL-NG.FOOTER-COMPONENT.CONTACT_LINKS.CONTACT",
              useTranslation: true,
              linkUrl:
                currentLanguageCode === "en"
                  ? `https://www.deutsch-uni.com/en/contact/`
                  : `https://www.deutsch-uni.com/de/kontakt/`,
            },
            {
              text: "GAST-PORTAL-NG.FOOTER-COMPONENT.CONTACT_LINKS.IMPRINT",
              useTranslation: true,
              linkUrl:
                currentLanguageCode === "en"
                  ? `https://www.deutsch-uni.com/en/imprint/`
                  : `https://www.deutsch-uni.com/de/impressum/`,
            },
            {
              text: "GAST-PORTAL-NG.FOOTER-COMPONENT.CONTACT_LINKS.DATA_PROTECTION",
              useTranslation: true,
              linkUrl:
                currentLanguageCode === "en"
                  ? `https://www.deutsch-uni.com/en/privacy/`
                  : `https://www.deutsch-uni.com/de/datenschutz/`,
            },
            {
              text: "GAST-PORTAL-NG.FOOTER-COMPONENT.CONTACT_LINKS.CONDITIONS",
              useTranslation: true,
              linkUrl:
                currentLanguageCode === "en"
                  ? `https://www.deutsch-uni.com/en/gtc/`
                  : `https://www.deutsch-uni.com/de/agb/`,
            },
          ],
          facebookUrl: "https://www.facebook.com/DeutschUniOnline/",
          youtubeUrl: "https://www.youtube.com/user/DeutschUniOnline",
          twitterUrl: "",
          instagramUrl: "https://www.instagram.com/gast_ev/",
          gastOfferText: { text: "GAST-PORTAL-NG.FOOTER-COMPONENT.DUO_OFFER_TEXT", useTranslation: true },
        } as FooterModel;
      }
      case "INL":
        // TODO 2025-03-31 Mateusz Warzyc: different footer structure port styles from tc portal
        return {} as FooterModel;
      case "DMAT":
        return {
          gastOfferText: { text: "GAST-PORTAL-NG.FOOTER-COMPONENT.D_MAT_OFFER_TEXT", useTranslation: true },
        } as FooterModel;
      case "DTZ":
        return {
          descriptionTitle: {
            text: "GAST-PORTAL-NG.FOOTER-COMPONENT.ABOUT_DTZ.DESCRIPTION_TITLE",
            useTranslation: true,
          },
          descriptionText: {
            text: "GAST-PORTAL-NG.FOOTER-COMPONENT.ABOUT_DTZ.DESCRIPTION_TEXT",
            useTranslation: true,
          },
          contactLinksTitle: { text: "GAST-PORTAL-NG.FOOTER-COMPONENT.CONTACT_LINKS.TITLE", useTranslation: true },
          contactLinks: [
            {
              text: "GAST-PORTAL-NG.FOOTER-COMPONENT.CONTACT_LINKS.IMPRINT",
              useTranslation: true,
              linkUrl: "https://www.gast.de/de/kontakt/impressum",
            },
            {
              text: "GAST-PORTAL-NG.FOOTER-COMPONENT.CONTACT_LINKS.DATA_PROTECTION",
              useTranslation: true,
              linkUrl: "https://www.gast.de/de/kontakt/datenschutz",
            },
          ],
          gastOfferText: { text: "   ", useTranslation: false },
        } as FooterModel;
      default:
        return gastFooterModel;
    }
  }

  #setSiteFavicon(product: ProductKey): void {
    const iconEl = document.getElementById("app-icon");
    if (iconEl) {
      switch (product) {
        case "GENERAL":
          iconEl.setAttribute("href", "./portal-assets/img/gast/gast-favicon.png");
          break;
        case "TESTDAF":
          iconEl.setAttribute("href", "./portal-assets/img/testdaf/testdaf-favicon.ico");
          break;
        case "TESTAS":
        case "DIGITAL_TESTAS":
          iconEl.setAttribute("href", "./portal-assets/img/testas/testas-favicon.ico");
          break;
        case "ONSET":
          iconEl.setAttribute("href", "./portal-assets/img/onset/onset-favicon.png");
          break;
        case "DUO":
          iconEl.setAttribute("href", "./portal-assets/img/duo/duo-favicon.ico");
          break;
        case "INL":
          iconEl.setAttribute("href", "./portal-assets/img/inl/inl-favicon.png");
          break;
        case "DMAT":
          iconEl.setAttribute("href", "./portal-assets/img/dmat/dmat-favicon.png");
          break;
        case "DTZ":
          iconEl.setAttribute("href", "./portal-assets/img/dtz/dtz-favicon.png");
          break;
        default:
          iconEl.setAttribute("href", "./portal-assets/img/gast/gast-favicon.png");
          break;
      }
    }
  }

  async #resolveFloatingContactPanelModel(
    product: ProductKey,
    currentLanguageCode: string
  ): Promise<FloatingContactPanelModel | undefined> {
    if (product === "DUO") {
      const contactImgTitle: TextModel = {
        text: "GAST-PORTAL-NG.FLOATING-CONTACT-PANEL.CONTACT_IMG_TEXT",
        useTranslation: true,
      };
      const suffix = await firstValueFrom<string>(
        this.#translationService.translateKeyDeferred("GAST-PORTAL-NG.DUO-URL.CONTACT", "kontakt")
      );
      const contactLink: LinkModel = {
        linkUrl: `https://www.deutsch-uni.com/${currentLanguageCode}/${suffix}/`,
        text: "GAST-PORTAL-NG.FLOATING-CONTACT-PANEL.CONTACT_LINK_TEXT",
        useTranslation: true,
      };
      const hiddenText: TextModel = {
        text: "GAST-PORTAL-NG.FLOATING-CONTACT-PANEL.HIDDEN_TEXT",
        useTranslation: true,
      };
      return {
        contactImgTitle,
        contactLink,
        emailIcon: "fal fa-envelope",
        hiddenText,
        phoneIcon: "fal fa-phone-volume",
        phoneNumberString: "+49 234 36715 500",
      };
    } else {
      return undefined;
    }
  }

  #resolveFloatingButtonsModel(product: ProductKey): FloatingButtonsModel | undefined {
    // 2025-03-28 Mateusz Warzyc: ONY USAGE FOUND ON onset.de
    if (product === "ONSET") {
      const emailButton = {
        linkUrl: "https://www.onset.de/de/home/institutionen/#contact",
        iconUrl: "portal-assets/img/mail_40px.png",
      };
      return {
        floatingButtons: [emailButton],
      };
    } else {
      return undefined;
    }
  }
}
